import { getDisplayData, renderItems } from "../common";
import * as PR from "../../prime-react/index";
import { dateTimeFormatComma, formatNumber, trimFileName } from "../../utils/reuse";
import { useToast } from "../../context/ToastContext";
import { downloadAttachment } from "../../utils/downloadAttachment";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./AlertDetails.scss";

const EventDetails = ({ data, isResponse, nonIbiaAttachments, alertType, alertId }) => {
    const { showToast } = useToast();
    const sessionData = useSelector((state) => state.auth.sessionData);
    const dispatch = useDispatch();
    const [expandedRows, setExpandedRows] = useState(null);

    const headers = useMemo(() => {
        return { sessionid: sessionData.sessionId };
    }, [sessionData.sessionId]);
    const fetchFileDetails = (file) => {
        const id = alertId ? data?.responseID : data?.legacyID;
        const alertID = alertId;
        const type = alertId ? "response" : "alert";
        downloadAttachment({
            file,
            id: id,
            type,
            alertID: alertID,
            headers,
            dispatch,
            showToast,
        });
    };
    const renderAlertDetailsLeft = (data, isResponse) => {
        const marketTags = data?.tags?.filter((c) => c.category === "market");
        const AthleteTags = data?.tags?.filter((c) => c.category !== "market");
        return (
            <>
                {data?.responseID && isResponse && (
                    <ul className="details-ul">
                        <li>Response ID</li>
                        <li>{data?.responseID}</li>
                    </ul>
                )}
                {data?.legacyID && (
                    <ul className="details-ul">
                        <li>Alert ID</li>
                        <li>{data?.legacyID}</li>
                    </ul>
                )}
                <ul className="details-ul">
                    <li>Type</li>
                    <li>{getDisplayData(alertType, "alertTypeFilter")}</li>
                </ul>
                {AthleteTags && (
                    <ul className="details-ul">
                        <li>Athlete/Team Tags</li>
                        <li>{renderItems(AthleteTags, "tags")}</li>
                    </ul>
                )}
                {marketTags && (
                    <ul className="details-ul">
                        <li>Market Tags</li>
                        <li>{renderItems(marketTags, "tags")}</li>
                    </ul>
                )}
                {data?.timeOfAlert && (
                    <ul className="details-ul">
                        <li>Alert raised</li>
                        <li>{getDisplayData(data?.timeOfAlert, "alertsRaised")}</li>
                    </ul>
                )}
                <ul className="details-ul">
                    <li>Channel</li>
                    <li>{renderItems(data?.channel, "channels")}</li>
                </ul>
            </>
        );
    };

    const renderAlertDetailsRight = (data, isResponse) => {
        const files = isResponse ? data.fileAttachment : nonIbiaAttachments;
        return (
            <>
                <ul className="details-ul">
                    <li>Time of bet</li>
                    <li>{renderItems(data?.timeOfBet, "timeOfBet")}</li>
                </ul>
                <ul className="details-ul">
                    <li>State of bet</li>
                    <li>{renderItems(data?.stateOfBet, "stateOfBetsFilter")}</li>
                </ul>

                <ul className="details-ul">
                    <li>Max bets</li>
                    <li>{data?.maxBets ? "Yes" : "No"}</li>
                </ul>
                <ul className="details-ul">
                    <li>Files</li>
                    <ul className="p-0 gap-2 files-download-ul">
                        {files?.map((file) => (
                            <li key={file.key} className="mr-2">
                                <PR.Button
                                    label={trimFileName(file?.name)}
                                    title={trimFileName(file?.name)}
                                    icon="pi pi-download"
                                    className="file-download"
                                    onClick={() => fetchFileDetails(file)}
                                />
                            </li>
                        ))}
                    </ul>
                </ul>
            </>
        );
    };
    const getTotalCustomers = (customerInfo) => {
        const customerCount = customerInfo?.customerDetails?.length ? customerInfo?.customerDetails?.reduce(
            (accumulator, currentValue) => accumulator + currentValue.count,
            0
        ) : "-";
        return customerCount !== "-" ? formatNumber(customerCount) : customerCount;
    };

    const formattedEvents = useMemo(() => {
        setExpandedRows(null);
        if (!data?.events) return [];
        return data.events.map((event, index) => {
            let e = { ...event };
            e.id = event.id || index;
            e.event = event.eventName;
            e.esportsTitleName = event?.esportsTitleName ?  event.esportsTitleName : '-';
            e.tournamentLevelName = event?.tournamentLevelName ?  event.tournamentLevelName : '-';
            e.turnover = Number.isFinite(event.turnover) ? `${event.turnover}%` : `-`;
            e.country = event.locationName;
            e.date = dateTimeFormatComma(event.dateTime);
            e.suspicious = isResponse || data?.alertStatus === 'suspicious' ? event?.suspicious : false;
            return e;
        });
    }, [data?.events, isResponse, data?.alertStatus]);
    const doesExistSuspiciousEvent = useMemo(() => {
        return formattedEvents?.some((event) => event?.suspicious) ?? false;
    }, [formattedEvents]);
    const rowExpansionTemplate = (eventData) => {
        return (
            <div className="suspicious-locations-expansion">
                <h5>Locations of suspicious customers</h5>
                {eventData.customerLocations && eventData.customerLocations.length > 0 ? (
                    <div className="grid">
                        {eventData.customerLocations.map((item, index) => (
                            <div className={`${isResponse ? 'col-3' : 'col-2'}`} key={index}>
                                <div className="box">
                                    <p>
                                        Country <br /> <span>{item?.country || '-'}</span>
                                    </p>
                                    {item?.state && (
                                        <p>
                                            State <br /> <span>{item?.state}</span>
                                        </p>
                                    )}
                                    <p>
                                        No. of customers <br />{" "}
                                        <span>{formatNumber(item?.count)}</span>
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="no-records-msg">No suspicious customer locations recorded for this event.</p>
                )}
            </div>
        );
    };

    const eventGridDisplay = (data, isResponse) => {
        return (
            <>
                <ul className="details-ul">
                    <li>No. of events</li>
                    <li>{formatNumber(data?.events?.length)}</li>
                </ul>
                <div className="card">
                    <PR.DataTable
                        value={formattedEvents}
                        stripedRows
                        className={`${isResponse ? 'alert-response-grid' : 'alert-main-grid'} data-table` }
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        rowClassName={(rowData) => rowData.suspicious ? 'suspicious-row' : 'non-suspicious-row'}
                        dataKey="id"
                    >
                        {doesExistSuspiciousEvent && (
                            <PR.Column expander style={{ width: '1rem', padding:'10px 5px' }} />
                        )}
                        <PR.Column
                            field="event"
                            header="Event Name"
                            style={{ width: isResponse ? '250px' : '300px', minWidth: isResponse ? '250px' : '105px'  }}
                        ></PR.Column>
                        <PR.Column
                            field="sportName"
                            header="Sport"
                            style={{ width: isResponse ? '120px' : '120px' }}
                        ></PR.Column>
                        {data?.events?.some(event => event.esportsTitleName) &&
                            <PR.Column
                                field="esportsTitleName"
                                header="Esports Title"
                                style={{ width: isResponse ? '140px' : '150px' }}
                            ></PR.Column>
                        }
                        <PR.Column
                            field="tournamentName"
                            header="League/Tournament"
                            style={{ width: isResponse ? '200px' : '250px' }}
                        ></PR.Column>
                        <PR.Column
                            field="tournamentLevelName"
                            header="Tournament Level"
                            style={{ width: isResponse ? '180px' : '190px' }}
                        ></PR.Column>
                        <PR.Column
                            field="locationName"
                            header="Location"
                            style={{ width: isResponse ? '100px' : '150px' }}
                        ></PR.Column>
                        <PR.Column
                            field="date"
                            header="Date & Time"
                            style={{ width: isResponse ? '190px' : '105px', minWidth: isResponse ? '190px' : '105px' }}
                        ></PR.Column>
                        <PR.Column
                            body={(data) => getDisplayData(data?.gender, "gender")}
                            header="Gender"
                            style={{ width: isResponse ? '50px' : '40px' }}
                        ></PR.Column>
                        <PR.Column
                            field="turnover"
                            header="Turnover"
                            style={{ width: isResponse ? '50px' : '40px' }}
                        ></PR.Column>
                        <PR.Column
                            body={(data) => (data?.suspicious ? <i className="pi pi-check-circle" style={{ color: "red", fontWeight: "bold" }}></i> : "")}
                            header="Suspicious"
                            style={{ width: isResponse ? '80px' : '80px' }}
                        ></PR.Column>
                    </PR.DataTable>
                </div>
                <div className="customerGrid">
                    <ul className="details-ul">
                        <li>Total customers</li>
                        <li>{getTotalCustomers(data.customerInfo)}</li>
                    </ul>
                    <span className="border-right"></span>
                    <ul className="details-ul">
                        <li>Type of customers</li>
                        <li className="capitalize">
                            {data.customerInfo
                                ? renderItems(
                                    data?.customerInfo?.customerType,
                                    "customerTypes"
                                )
                                : "-"}
                        </li>
                    </ul>
                </div>
                <div className="grid">
                    {data?.customerInfo?.customerDetails?.map(
                        (item, index) => (
                            <div className={`${isResponse ? 'col-3': 'col-2'}`} key={index}>
                                <div className="box">
                                    <p>
                                        Country <br /> <span>{item?.country}</span>
                                    </p>
                                    {item?.state && (
                                        <p>
                                            State <br /> <span>{item?.state}</span>
                                        </p>
                                    )}
                                    <p>
                                        No. of customers <br />{" "}
                                        <span>{formatNumber(item?.count)}</span>
                                    </p>
                                </div>
                            </div>
                        )
                    )}
                </div>
            </>
        )

    }

    return (
        <>
            <div className="col-12 grid justify-content-between mt-4">
                <div className="col-5 border-right">
                    {renderAlertDetailsLeft(data, isResponse)}
                </div>
                <div className="col-6">
                    {renderAlertDetailsRight(data, isResponse)}
                </div>
            </div>
            <div className="col-12">
                {eventGridDisplay(data, isResponse)}
            </div>
        </>
    );
};
export default EventDetails;
